import React, { Component } from 'react';
import gql from 'graphql-tag';
import { compose, graphql } from 'react-apollo';
import ReCAPTCHA from 'react-google-recaptcha';
import './SiteUsageContinuation.css';

class SiteUsageContinuation extends Component {
  constructor(props) {
    super(props);

    this.START_DELAY_MS = 2 * 1000;
    this.CHALLENGE_RESPONSE_TIMEOUT = 60 * 1000;

    this.siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;
    this.captchaTokenReceived = this.captchaTokenReceived.bind(this);
    this.continuePlaying = this.continuePlaying.bind(this);

    this.unsubscribeFromCanContinueData = () => {};

    this.state = {
      willShow: false,
      show: false,
      interacted: false
    };

    this.timeouts = [];
  }

  startShow() {
    this.setState({
      willShow: true,
      show: false,
      interacted: false
    });
  }

  componentDidMount() {
    this.unsubscribeFromCanContinueData =
      this.props.subscribeToCanContinueData();
  }

  componentWillUnmount() {
    this.unsubscribeFromCanContinueData();
    this.unsubscribeFromCanContinueData = () => {};

    this.timeouts.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    this.timeouts = [];
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps &&
      prevProps.data && // query pending
      ((prevProps.data.canContinuePlay &&
        prevProps.data.canContinuePlay.canContinue) || // previous received data that said canContinue
        (prevProps.data.loading &&
          this.props.data
            .canContinuePlay)) /* or previously was loading and just received data */ &&
      !this.props.data.canContinuePlay.canContinue &&
      !this.state.show &&
      !this.state.willShow // cannot continue and did not start feature
    ) {
      // reset for this latest showing
      this.startShow();
      this.timeouts.push(
        setTimeout(() => {
          this.setState({ willShow: false, show: true });
        }, this.START_DELAY_MS)
      );
    } else if (
      prevProps &&
      prevProps.data &&
      prevProps.data.canContinuePlay &&
      !prevProps.data.canContinuePlay.canContinue &&
      this.props.data.canContinuePlay.canContinue
    ) {
      this.setState({ willShow: false, show: false });
    }
  }

  captchaTokenReceived(token) {
    this.props.continuationAck(token);
  }

  continuePlaying() {
    if (this.useRecaptcha()) {
      this.captcha.execute();
      this.timeouts.push(
        setTimeout(() => {
          this.captcha.reset();
          this.setState({ interacted: false });
        }, this.CHALLENGE_RESPONSE_TIMEOUT)
      );
    } else {
      this.props.continuationAck();
    }

    this.setState({ interacted: true });
  }

  useRecaptcha() {
    return this.props.data.canContinuePlay.requiresChallenge;
  }

  render() {
    const { loading, error } = this.props.data;

    if (loading || error) {
      return null;
    }

    return (
      <div
        className={
          'site-usage-continuation' + (this.state.willShow ? ' willShow' : '')
        }
      >
        <div
          className={
            'site-usage-continuation-window' + (this.state.show ? ' show' : '')
          }
        >
          <div className="usage-window-bg">
            <div className="vector-star-b-top-left" />
            <div className="vector-star-s-top-left" />
            <div className="vector-star-b-top-right" />
            <div className="vector-star-s-bottom-left" />
            <div className="vector-star-b-bottom-left" />
            <div className="usage-thank-you"> Thank you for playing! </div>
            <div className="usage-text-content upper">
              We hope you're loving the games. Check out our{' '}
              <span>tournaments</span>, <span>auctions</span>, and{' '}
              <span>drawings</span> for even more fun!
            </div>
            <div className="usage-button-group">
              <button
                className="btn usage-continue-btn"
                onClick={this.continuePlaying}
                type="button"
                disabled={this.state.interacted}
              >
                Continue
              </button>
            </div>
            <div className="usage-text-content lower">
              {'This site is protected by reCAPTCHA and the Google '}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>
              {' and '}
              <a href="https://policies.google.com/terms">Terms of Service</a>
              {' apply.'}
            </div>
          </div>
        </div>
        <ReCAPTCHA
          ref={elem => {
            this.captcha = elem;
          }}
          sitekey={this.siteKey}
          onChange={token => {
            this.captchaTokenReceived(token);
            this.captcha.reset();
          }}
          theme="dark"
          onExpired={() => {
            this.setState({ interacted: false });
          }}
          size="invisible"
          badge="inline"
        />
      </div>
    );
  }
}

const canContinueQuery = gql`
  query CanContinuePlay {
    canContinuePlay {
      canContinue
      requiresChallenge
    }
  }
`;

const SUBSCRIPTION_CANCONTINUE = gql`
  subscription CanContinuePlayUpdates {
    canContinuePlayUpdates {
      canContinue
      requiresChallenge
    }
  }
`;

const canContinueOptions = {
  options: { fetchPolicy: 'cache-and-network' },
  props: ({ ownProps, data }) => {
    return {
      data,
      subscribeToCanContinueData: () => {
        return data.subscribeToMore({
          document: SUBSCRIPTION_CANCONTINUE,
          updateQuery: (prev, { subscriptionData }) => {
            const canContinuePlay = subscriptionData.data;
            if (canContinuePlay !== undefined) {
              return Object.assign({}, prev, {
                canContinuePlay: Object.assign({}, prev.canContinuePlay, {
                  canContinue: canContinuePlay.canContinue,
                  requiresChallenge: canContinuePlay.requiresChallenge
                })
              });
            }
            return null;
          }
        });
      }
    };
  }
};

const GameplayContinuationAck = gql`
  mutation MyContinueAck($input: CaptchaResolve) {
    acknowledgeContinuation(input: $input)
  }
`;

const GameplayContinuationAckOptions = {
  props: ({ mutate }) => ({
    continuationAck: token => {
      if (token) {
        return mutate({
          variables: { input: { captchaToken: token } }
        });
      } else {
        return mutate();
      }
    }
  })
};

const SiteUsageWithData = compose(
  graphql(GameplayContinuationAck, GameplayContinuationAckOptions),
  graphql(canContinueQuery, canContinueOptions)
)(SiteUsageContinuation);

export default SiteUsageWithData;
